import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  SimpleForm,
  useDataProvider,
  useNotify,
} from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core";
import DownloadIcon from "@material-ui/icons/GetApp";
import { useFormState } from "react-final-form";
import { Parser } from "json2csv";
import fileDownload from "js-file-download";
import ExportCommercialDocumentInput from "./ExportCommercialDocumentInput";
import {
  exportCommercialDocumentValidation,
  generateExportCommercialDocumentFilename
} from "../tools";

interface IExportButton {
  documentType: "Invoice" | "CreditNote";
  setIsLoading?: (isLoading: boolean) => void;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  form: {
    "& .MuiCardContent-root": {
      padding: 0,
    },
  },
  cancelButton: {
    backgroundColor: "#9e9e9e"
  },
  exportButton: {
    float: "right"
  },
});

const ExportButton = ({ setIsLoading, documentType }: IExportButton) => {
  const { values } = useFormState();
  const notify = useNotify();
  const { getRecordForCSV, getOne } = useDataProvider();

  const [isCSVLoading, setCSVLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [progress, setProgress] = useState<{
    current: number | null;
    total: number | null;
  }>({
    current: 0,
    total: 0,
  });

  const documentTypeLabel = documentType === "Invoice" ? "Invoice" : "Credit Note";

  const exportCSV = async () => {
    if (isCSVLoading) return;

    const filter: any = {
      Type: documentType,
      StatusIn: ["Draft", "Unposted", "Posted"],
      IssuedFromDate: values.startDate,
      IssuedToDate: values.endDate,
    };

    const body = {
      ...values,
      commercialDocumentType: documentType,
    }

    if (values.isFilterBySchool && values.schoolId) {
      try {
        const { data: school } = await getOne("schools", { id: values.schoolId });

        if (!school || !school.mbcCode) {
          notify("Selected school is missing MBC Code", "warning");
          return;
        }

        filter.SchoolCode = school.mbcCode;
      } catch (e) {
        notify("Failed to fetch school info", "warning");
        return;
      }
    }

    if (values.isFilterByStatus) {
      filter.StatusIn = values.status;
    }

    if (
      values.isFilterByStatus &&
      documentType === "CreditNote" &&
      values.status.includes("Posted")
    ) {
      filter.IsTaggedCreditNote = values.isTaggedCreditNote;
    }

    if (values.isFilterByDescription) {
      filter.Description = values.description;
    }

    getRecordForCSV("commercialDocuments", {
      filter,
      body,
      callback: ({ isLoading, loadedRecordCount, totalRecords }: any) => {
        // Only update loading state once at start and end
        if (isLoading !== undefined) {
          setCSVLoading(isLoading);
          setIsLoading && setIsLoading(isLoading);
        }

        setProgress({
          current: loadedRecordCount,
          total: totalRecords,
        });
      },
    })
      .then(({ data }: any) => {
        const parser = new Parser();

        if (!data || data.length === 0) {
          notify(`No ${documentTypeLabel.toLowerCase()} found`, "warning");
          return;
        }

        const csvData = data.flat();

        const fileName = generateExportCommercialDocumentFilename({
          ...values,
          commercialDocumentTypeLabel: documentTypeLabel,
        } as formExportCommercialDocumentValues);

        fileDownload(parser.parse(csvData), fileName);
      })
      .catch((e: any) => {
        notify(`${e.message}`, "warning");
      })
      .finally(() => {
        // Reset loading state and progress regardless of success or failure
        setCSVLoading(false);
        setIsLoading && setIsLoading(false);
        setProgress({
          current: null,
          total: null,
        });
      });
  };

  const labelButton = useMemo(() => {
    if (!isCSVLoading) {
      return "Export";
    }

    if (progress.current === null && progress.total === null) {
      return "Fetching Commercial Documents";
    }

    if (progress.current !== null && progress.total !== null) {
      return `Fetching Data ${progress.current}/${progress.total}`;
    }

    return "Downloading CSV";
  }, [isCSVLoading, progress]);

  useEffect(() => {
    const isFormInvalid = exportCommercialDocumentValidation({
      ...values,
      commercialDocumentType: documentType,
    } as formExportCommercialDocumentValues);

    setIsDisabled(isFormInvalid);
  }, [values]);

  return (
    <Button
      onClick={exportCSV}
      label={labelButton}
      color="primary"
      disabled={isDisabled}
      autoFocus
    />
  );
};

const ExportCommercialDocumentButton = ({ documentType }: IExportButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        label="Export"
        onClick={handleOpenDialog}
        className={classes.exportButton}
      >
        <DownloadIcon />
      </Button>

      <SimpleForm
        toolbar={null}
        className={classes.form}
      >
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="md"
        >
          <DialogTitle id="alert-dialog-title">
            Export {documentType === "Invoice" ? "Invoice" : "Credit Note"}
          </DialogTitle>

          <DialogContent>
            <DialogContentText color="initial">
              <Typography variant="body1" gutterBottom>
                Please select the {documentType === "Invoice" ? "invoice" : "credit note"} issued date
                range you want to export. You may also configure additional export options below by toggling
                each filter as needed.
              </Typography>
            </DialogContentText>

            <ExportCommercialDocumentInput
              documentType={documentType}
              isDisabled={isLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              color="secondary"
              label="Cancel"
              disabled={isLoading}
              className={classes.cancelButton}
            />
            <ExportButton
              documentType={documentType}
              setIsLoading={(value) => {
                setIsLoading(value);
              }}
            />
          </DialogActions>
        </Dialog>
      </SimpleForm>
    </>
  )
}

export default ExportCommercialDocumentButton;